const CHOSEN_COUNTRY_LANG = 'chosenCountryLang';

// A mimic of the html5 storage objects
class FakeStorage {
  data: { [key: string]: string | null } = {};
  getItem(key: string) {
    return this.data[key];
  }
  removeItem(key: string) {
    delete this.data[key];
  }
  setItem(key: string, value: string) {
    this.data[key] = value;
  }
  clear() {
    this.data = {};
  }
  get length() {
    return Object.keys(this.data).length;
  }
}

// MDN recommended function to check browser storage availability, with small adjustment for Android
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
const _verifyStorage = (local = false) => {
  let storage;

  try {
    storage = window[local ? 'localStorage' : 'sessionStorage'];
    const x = '__storage_test__';

    // Extra check
    if (storage === null || storage === undefined) {
      return false;
    }

    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
};

// Setup objects for local and session storage
const local = _verifyStorage(true) ? window.localStorage : new FakeStorage();
const session = _verifyStorage(false)
  ? window.sessionStorage
  : new FakeStorage();

export const setObject = (key: string, value: any) => {
  local.setItem(key, JSON.stringify(value));
};

export const getObject = (key: string) => {
  const value = local.getItem(key);

  return value && JSON.parse(value);
};

export const removeObject = (key: string) => {
  local.removeItem(key);
};

export const setSessionObject = (key: string, value: any) => {
  session.setItem(key, JSON.stringify(value));
};

export const getSessionObject = (key: string) => {
  const value = session.getItem(key);

  return value && JSON.parse(value);
};

export const removeSessionObject = (key: string) => {
  session.removeItem(key);
};

export const clearSession = () => {
  session.clear();
};

export const clearSessionArray = (keysToRemove: string[]) => {
  keysToRemove.forEach((k) => session.removeItem(k));
};

export const updateChosenCountry = (country: string) => {
  const current = getObject(CHOSEN_COUNTRY_LANG) || {};

  current.country = country;

  setObject(CHOSEN_COUNTRY_LANG, current);
};

export const updateChosenLang = (language: string) => {
  const current = getObject(CHOSEN_COUNTRY_LANG) || {};

  current.language = language;

  setObject(CHOSEN_COUNTRY_LANG, current);
};

export const updateChosenCountryLang = (country: string, lang: string) => {
  updateChosenCountry(country);
  updateChosenLang(lang);
};

export const getCurrentCountry = () => {
  const current = getObject(CHOSEN_COUNTRY_LANG);

  return current && current.country;
};

export const getCookie = (key: string) => {
  const nameEQ = key + '=';
  const ca = window.document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];

    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }

    if (c.indexOf(nameEQ) === 0) {
      const value = c.substring(nameEQ.length, c.length);

      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
  }

  return null;
};

export const setCookie = (
  key: string,
  value: any,
  timeMs: number = 3600000,
) => {
  let expires = '';

  const date = new Date();

  date.setTime(date.getTime() + timeMs);
  expires = '; expires=' + date.toUTCString();

  const jsonValue = JSON.stringify(value);

  window.document.cookie = key + '=' + (jsonValue || '') + expires + '; path=/';
};

export const clearCookie = (key: string) => {
  window.document.cookie = key + '=; Max-Age=0; path=/';
};

export default {
  clearCookie,
  clearSession,
  getCookie,
  getObject,
  getSessionObject,
  removeObject,
  removeSessionObject,
  setCookie,
  setObject,
  setSessionObject,
};
